import { regulatoryWatchPaths } from '@/paths';
import { Button } from '@/components/ui/button';
import { CountryFilter } from '@/components/ui/country-select';
import {
  DateRangeSelect,
  type DateRange,
} from '@/components/ui/date-range-select';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Text } from '@/components/ui/text';
import { useSeenUpdates } from '@/hooks/use-seen-updates';
import { useDebounce } from '@/hooks/useDebounce';
import { useListItemExitAnimation } from '@/hooks/useListItemExitAnimation';
import { ExitCollapseRow } from '@/components/animation/ExitCollapseRow';
import { REGULATORY_WATCH_BOTTOM_BAR_HEIGHT_PX } from '@/components/table/constants';
import {
  useBulkApplyContextStatusesService,
  useRegulatoryWatchService,
  useSourcesService,
  useUpdateContextStatusService,
} from '@/services/regulatoryWatch';
import { useCurrentWorkspaceService } from '@/services/workspaces';
import { cn } from '@/utils/classUtils';
import {
  ApplicabilityStatus,
  ListUpdatesQuery,
  ListUpdatesResponse,
} from '@ditto/shared';
import { toast } from 'sonner';
import {
  ArrowUpDown,
  Check,
  Filter,
  Loader2,
  PartyPopper,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  PRIORITY_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  STATUS_LABELS,
  UPDATE_TYPE_OPTIONS,
} from '../labels';
import { FeedMissingSubscriptionHintOverlay } from './FeedMissingSubscriptionHint';
import { UpdatesSearchField } from './UpdatesSearchField';
import {
  REVIEW_SORT_MODE_LABELS,
  ReviewSortMode,
  sortReviewUpdatesByDate,
  sortReviewUpdatesByPriority,
} from './sortUtils';
import { StatusTabs, STATUS_TAB_LABELS, type StatusTab } from './StatusTabs';
import { UpdateCard } from './UpdateCard';
import { UpdateCardSkeleton } from './UpdateCardSkeleton';
import {
  useRegulatoryWatchScrollSession,
  useRegulatoryWatchSearchSession,
} from '../hooks/useRegulatoryWatchPageSession';

const REVIEW_SORT_OPTIONS: FilterOption[] = [
  { label: 'Priority', value: 'priority' },
  { label: 'Date', value: 'date' },
];

/** Matches footer height — list padding so the last card clears the bar */
const REVIEW_FEED_FOOTER_HEIGHT_PX = REGULATORY_WATCH_BOTTOM_BAR_HEIGHT_PX;

/** Matches top-of-feed tab descriptor (TabDescription / Figma). */
const FEED_DESCRIPTOR_CLASS =
  'max-w-[615px] text-xs italic text-neutral-500 tracking-[0.18px] text-center';

const TO_REVIEW_FEED_END_COPY =
  "You've reached the end of your updates. Showing updates from December 2025 onwards.";

/** Fade + slide left (matches `animate-newsfeed-card-exit` duration in tailwind.config.js). */
const NEWSFEED_EXIT_ANIM_MS = 550;
/** Blank space after the card is gone, before the list collapses. */
const NEWSFEED_EXIT_HOLD_MS = 420;
/** Row height animates to 0 so cards below slide up. */
const NEWSFEED_EXIT_COLLAPSE_MS = 780;

type StatusChangeMeta = {
  prevStatus: ApplicabilityStatus;
  newStatus: ApplicabilityStatus;
};

interface ReviewCardListProps {
  isBatchAssessing?: boolean;
  assessmentProgress?: number;
  bannerTotalUpdates?: number;
  bannerOutOfScopeCount?: number;
}

const REVIEW_STATUS_TABS: readonly StatusTab[] = [
  'to_review',
  'applicable',
  'not_applicable',
  'out_of_scope',
];

const parseCsvParam = (value: string | null): string[] =>
  value
    ? value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
    : [];

const parseStatusTabParam = (value: string | null): StatusTab =>
  REVIEW_STATUS_TABS.includes(value as StatusTab)
    ? (value as StatusTab)
    : 'to_review';

const REVIEW_SORT_MODES: readonly ReviewSortMode[] = ['priority', 'date'];

const parseSortModeParam = (value: string | null): ReviewSortMode =>
  REVIEW_SORT_MODES.includes(value as ReviewSortMode)
    ? (value as ReviewSortMode)
    : 'priority';

export const ReviewCardList: FC<ReviewCardListProps> = ({
  isBatchAssessing,
  assessmentProgress = 0,
  bannerTotalUpdates = 0,
  bannerOutOfScopeCount = 0,
}) => {
  // Track transition from assessing → done to trigger slide-in animation + banner
  const wasAssessingRef = useRef(false);
  const [animateCards, setAnimateCards] = useState(false);
  const [showBanner, setShowBanner] = useState(false);

  // Track which update IDs are new during batch assessment for slide-in
  const [newBatchIds, setNewBatchIds] = useState<Set<string>>(new Set());
  const knownIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (wasAssessingRef.current && !isBatchAssessing) {
      setAnimateCards(true);
      setShowBanner(true);
      knownIdsRef.current.clear();
      setNewBatchIds(new Set());
      const timeout = setTimeout(() => setAnimateCards(false), 3000);
      return () => clearTimeout(timeout);
    }
    if (!isBatchAssessing) {
      knownIdsRef.current.clear();
      setNewBatchIds(new Set());
    }
    wasAssessingRef.current = !!isBatchAssessing;
  }, [isBatchAssessing]);

  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sourceOptionsForFilter, geographyCodes, expandSourceFilter } =
    useSourcesService();
  const { currentWorkspace } = useCurrentWorkspaceService();

  const activeTab = parseStatusTabParam(searchParams.get('reviewStatus'));
  const sortMode = parseSortModeParam(searchParams.get('reviewSort'));
  const showFilters = searchParams.get('filtersOpen') === '1';
  const searchQuery = searchParams.get('search') ?? '';
  const selectedTypes = parseCsvParam(searchParams.get('type'));
  const selectedDocumentTypes = parseCsvParam(searchParams.get('documentType'));
  const selectedSources = parseCsvParam(searchParams.get('source'));
  const selectedGeographies = parseCsvParam(searchParams.get('geography'));
  const selectedPriorities = parseCsvParam(searchParams.get('priority'));
  const selectedAssignees = parseCsvParam(searchParams.get('assignees'));
  const publicationDateRange: DateRange = {
    from: searchParams.get('publicationDateFrom') ?? undefined,
    to: searchParams.get('publicationDateTo') ?? undefined,
  };
  const effectiveDateRange: DateRange = {
    from: searchParams.get('effectiveDateFrom') ?? undefined,
    to: searchParams.get('effectiveDateTo') ?? undefined,
  };

  const debouncedSearch = useDebounce(searchQuery, 300);

  const hasActiveFilters = useMemo(
    () =>
      searchQuery.length > 0 ||
      selectedTypes.length > 0 ||
      selectedDocumentTypes.length > 0 ||
      selectedSources.length > 0 ||
      selectedGeographies.length > 0 ||
      selectedPriorities.length > 0 ||
      selectedAssignees.length > 0 ||
      publicationDateRange.from !== undefined ||
      publicationDateRange.to !== undefined ||
      effectiveDateRange.from !== undefined ||
      effectiveDateRange.to !== undefined,
    [
      searchQuery,
      selectedTypes,
      selectedDocumentTypes,
      selectedSources,
      selectedGeographies,
      selectedPriorities,
      selectedAssignees,
      publicationDateRange,
      effectiveDateRange,
    ]
  );

  const clearAllFilters = useCallback(() => {
    setSearchParams(
      prev => {
        const next = new URLSearchParams(prev);
        [
          'search',
          'type',
          'documentType',
          'source',
          'geography',
          'priority',
          'reviewStatus',
          'publicationDateFrom',
          'publicationDateTo',
          'effectiveDateFrom',
          'effectiveDateTo',
        ].forEach(key => next.delete(key));
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const updateSearchParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          updater(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setSingleParam = useCallback(
    (key: string, value: string | undefined) => {
      updateSearchParams(params => {
        if (value) {
          params.set(key, value);
          return;
        }
        params.delete(key);
      });
    },
    [updateSearchParams]
  );

  const setCsvParam = useCallback(
    (key: string, values: string[]) => {
      setSingleParam(key, values.length > 0 ? values.join(',') : undefined);
    },
    [setSingleParam]
  );

  const setDateRangeParams = useCallback(
    (fromKey: string, toKey: string, value: DateRange) => {
      updateSearchParams(params => {
        if (value.from) {
          params.set(fromKey, value.from);
        } else {
          params.delete(fromKey);
        }
        if (value.to) {
          params.set(toKey, value.to);
        } else {
          params.delete(toKey);
        }
      });
    },
    [updateSearchParams]
  );

  // Single fetch — no status filter, client-side filtering by tab
  const filters: ListUpdatesQuery = useMemo(() => {
    const q: ListUpdatesQuery = {};
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    if (selectedTypes.length > 0) q.type = selectedTypes.join(',');
    if (selectedDocumentTypes.length > 0)
      q.documentType = selectedDocumentTypes.join(',');
    if (selectedSources.length > 0) {
      const expandedSources = expandSourceFilter(selectedSources);
      q.source = expandedSources.join(',');
    }
    if (selectedGeographies.length > 0)
      q.geography = selectedGeographies.join(',');
    if (selectedPriorities.length > 0)
      q.priority = selectedPriorities.join(',');
    if (selectedAssignees.length > 0) q.assignees = selectedAssignees.join(',');
    if (publicationDateRange.from)
      q.publicationDateFrom = publicationDateRange.from;
    if (publicationDateRange.to) q.publicationDateTo = publicationDateRange.to;
    if (effectiveDateRange.from) q.effectiveDateFrom = effectiveDateRange.from;
    if (effectiveDateRange.to) q.effectiveDateTo = effectiveDateRange.to;
    q.subscribedOnly = 'true';
    return q;
  }, [
    debouncedSearch,
    selectedTypes,
    selectedDocumentTypes,
    selectedSources,
    expandSourceFilter,
    selectedGeographies,
    selectedPriorities,
    selectedAssignees,
    publicationDateRange,
    effectiveDateRange,
  ]);

  const { updates, isPending, error } = useRegulatoryWatchService(filters, {
    refetchInterval: 3000,
  });

  useRegulatoryWatchSearchSession('newsfeed');
  const feedScrollRef = useRef<HTMLDivElement>(null);
  const feedReadyForScroll =
    !isPending && !(isBatchAssessing && assessmentProgress < 20);
  useRegulatoryWatchScrollSession(
    'newsfeed',
    feedScrollRef,
    feedReadyForScroll
  );

  const { updateStatus } = useUpdateContextStatusService();
  const { bulkApplyStatuses, isBulkApplying } =
    useBulkApplyContextStatusesService();
  const { hasSeen, markSeen } = useSeenUpdates();

  const toReviewUnreadCount = useMemo(() => {
    let n = 0;
    for (const u of updates) {
      const s = u.context?.status ?? 'to_review';
      if (s === 'to_review' && !hasSeen(u.id)) {
        n++;
      }
    }
    return n;
  }, [updates, hasSeen]);

  // Client-side filter by active tab + sort
  // During batch assessment (>=20%), hide updates still generating so they
  // slide in one-by-one as their assessment completes
  const showingProgressiveCards = isBatchAssessing && assessmentProgress >= 20;
  const filteredUpdates = useMemo(() => {
    let filtered = updates.filter(u => {
      const s = u.context?.status ?? 'to_review';
      return s === activeTab;
    });
    if (showingProgressiveCards) {
      filtered = filtered.filter(
        u => u.context?.aiImpactStatus !== 'generating'
      );
    }
    const comparator =
      sortMode === 'priority'
        ? sortReviewUpdatesByPriority
        : sortReviewUpdatesByDate;
    return [...filtered].sort(comparator);
  }, [updates, activeTab, sortMode, showingProgressiveCards]);

  const { displayItems: newsfeedDisplayItems, startExit } =
    useListItemExitAnimation<ListUpdatesResponse, StatusChangeMeta>({
      getId: u => u.id,
      liveItems: filteredUpdates,
      timings: {
        animate: NEWSFEED_EXIT_ANIM_MS,
        hold: NEWSFEED_EXIT_HOLD_MS,
        collapse: NEWSFEED_EXIT_COLLAPSE_MS,
      },
      onComplete: (_id, meta) => {
        const fromLabel = STATUS_LABELS[meta.prevStatus] ?? meta.prevStatus;
        const toLabel = STATUS_LABELS[meta.newStatus] ?? meta.newStatus;
        toast.success(`Status changed from ${fromLabel} to ${toLabel}`, {
          position: 'bottom-right',
          duration: 4000,
        });
      },
    });

  const handleStatusChangeFromFeed = useCallback(
    (update: ListUpdatesResponse, newStatus: ApplicabilityStatus) => {
      if (!update.context) return;
      const prevStatus = (update.context.status ??
        'to_review') as ApplicabilityStatus;
      if (prevStatus === newStatus) return;

      const { cancel } = startExit(update, { prevStatus, newStatus });

      updateStatus(update.context.id, newStatus, undefined, {
        onError: cancel,
      });
    },
    [updateStatus, startExit]
  );

  const tabCardCount = filteredUpdates.length;

  // Detect newly appearing updates during batch assessment for slide-in
  useEffect(() => {
    if (!isBatchAssessing || assessmentProgress < 20) return;
    const currentIds = new Set(filteredUpdates.map(u => u.id));
    const freshIds = new Set<string>();
    for (const id of currentIds) {
      if (!knownIdsRef.current.has(id)) {
        freshIds.add(id);
      }
    }
    if (freshIds.size > 0) {
      setNewBatchIds(prev => {
        const next = new Set(prev);
        for (const id of freshIds) next.add(id);
        return next;
      });
      // Clear animation class after it completes
      const timeout = setTimeout(() => {
        setNewBatchIds(prev => {
          const next = new Set(prev);
          for (const id of freshIds) next.delete(id);
          return next;
        });
      }, 500);
      // Update known IDs
      for (const id of currentIds) knownIdsRef.current.add(id);
      return () => clearTimeout(timeout);
    }
    // Update known IDs even if no new ones
    for (const id of currentIds) knownIdsRef.current.add(id);
  }, [filteredUpdates, isBatchAssessing, assessmentProgress]);

  // Filter configs (same pattern as UpdatesTable)
  const sortedFilters = useMemo(() => {
    type FilterConfig =
      | {
          key: string;
          label: string;
          options: FilterOption[];
          selected: string[];
          onChange: (selected: string[]) => void;
          type: 'options';
        }
      | {
          key: string;
          label: string;
          countries?: string[];
          selected: string[];
          onChange: (selected: string[]) => void;
          type: 'country';
        }
      | {
          key: string;
          label: string;
          value: DateRange;
          onChange: (value: DateRange) => void;
          type: 'dateRange';
        };

    const filterConfigs: FilterConfig[] = [
      {
        key: 'priority',
        label: 'Priority',
        options: PRIORITY_OPTIONS,
        selected: selectedPriorities,
        onChange: selected => setCsvParam('priority', selected),
        type: 'options',
      },
      {
        key: 'type',
        label: 'Update type',
        options: UPDATE_TYPE_OPTIONS,
        selected: selectedTypes,
        onChange: selected => setCsvParam('type', selected),
        type: 'options',
      },
      {
        key: 'documentType',
        label: 'Document type',
        options: SOURCE_TYPE_OPTIONS,
        selected: selectedDocumentTypes,
        onChange: selected => setCsvParam('documentType', selected),
        type: 'options',
      },
      {
        key: 'geography',
        label: 'Regulatory region',
        countries: geographyCodes,
        selected: selectedGeographies,
        onChange: selected => setCsvParam('geography', selected),
        type: 'country',
      },
      {
        key: 'source',
        label: 'Source',
        options: sourceOptionsForFilter,
        selected: selectedSources,
        onChange: selected => setCsvParam('source', selected),
        type: 'options',
      },
      {
        key: 'assignees',
        label: 'Assigned to',
        options: (currentWorkspace?.members ?? []).map(m => ({
          label: `${m.firstName} ${m.lastName}`.trim() || m.email,
          value: m.id,
        })),
        selected: selectedAssignees,
        onChange: selected => setCsvParam('assignees', selected),
        type: 'options',
      },
      {
        key: 'publicationDate',
        label: 'Publication date',
        value: publicationDateRange,
        onChange: value =>
          setDateRangeParams('publicationDateFrom', 'publicationDateTo', value),
        type: 'dateRange',
      },
      {
        key: 'effectiveDate',
        label: 'Effective date',
        value: effectiveDateRange,
        onChange: value =>
          setDateRangeParams('effectiveDateFrom', 'effectiveDateTo', value),
        type: 'dateRange',
      },
    ];

    const isActive = (filter: FilterConfig) => {
      if (filter.type === 'options' || filter.type === 'country')
        return filter.selected.length > 0;
      if (filter.type === 'dateRange')
        return !!(filter.value.from || filter.value.to);
      return false;
    };

    const active = filterConfigs.filter(isActive);
    const inactive = filterConfigs.filter(f => !isActive(f));

    return [...active, ...inactive];
  }, [
    selectedTypes,
    selectedDocumentTypes,
    selectedSources,
    selectedGeographies,
    selectedPriorities,
    selectedAssignees,
    publicationDateRange,
    effectiveDateRange,
    sourceOptionsForFilter,
    geographyCodes,
    currentWorkspace?.members,
    setCsvParam,
    setDateRangeParams,
  ]);

  const handleCardClick = (update: ListUpdatesResponse, index: number) => {
    markSeen(update.id);
    navigate(
      `/workspace/${workspaceUname}/regulatory-watch/updates/${update.id}${location.search}`,
      {
        state: {
          updateIds: filteredUpdates.map(u => u.id),
          currentIndex: index,
          backPath: `/workspace/${workspaceUname}/regulatory-watch/${regulatoryWatchPaths.newsfeed}`,
          backLabel: 'My updates by status',
        },
      }
    );
  };

  if (error) {
    return (
      <Text variant="body-sm" color="primary">
        An error occurred while loading updates.
      </Text>
    );
  }

  return (
    <div className="flex flex-col min-w-0 min-h-0 flex-1">
      {/* Controls section */}
      <div className="shrink-0 flex flex-col items-center gap-4 px-6 py-4 border-b border-neutral-200">
        <div className="relative flex w-full min-h-9 items-center justify-center">
          <StatusTabs
            activeTab={activeTab}
            onTabChange={tab =>
              setSingleParam(
                'reviewStatus',
                tab === 'to_review' ? undefined : tab
              )
            }
            toReviewUnreadCount={toReviewUnreadCount}
          />
        </div>

        {/* Assessment complete banner */}
        {showBanner && (
          <AssessmentCompleteBanner
            totalUpdates={bannerTotalUpdates}
            relevantCount={bannerTotalUpdates - bannerOutOfScopeCount}
            onDismiss={() => setShowBanner(false)}
          />
        )}

        {/* Row 2: Search (left) + hide filters + sort (right) */}
        <div className="relative w-full min-h-10 flex justify-center items-center">
          <div className="absolute left-0 top-1/2 -translate-y-1/2">
            <UpdatesSearchField
              value={searchQuery}
              onChange={v => setSingleParam('search', v || undefined)}
            />
          </div>
          <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-2">
            <button
              className="flex items-center gap-2 px-2 py-1 text-sm font-semibold text-neutral-900 hover:text-neutral-700 transition-colors whitespace-nowrap"
              onClick={() =>
                setSingleParam('filtersOpen', showFilters ? undefined : '1')
              }
            >
              {showFilters ? 'Hide filters' : 'Show filters'}
              <Filter className="h-4 w-4" />
            </button>
            <SortModeFilter
              value={sortMode}
              onChange={value =>
                setSingleParam(
                  'reviewSort',
                  value === 'priority' ? undefined : value
                )
              }
            />
          </div>
        </div>

        {/* Row 3 (expanded): filter pills only */}
        {showFilters && (
          <FlexRow gap="2.5" alignItems="center" className="flex-wrap w-full">
            {sortedFilters.map(filter =>
              filter.type === 'options' ? (
                <OptionFilter
                  key={filter.key}
                  label={filter.label}
                  options={filter.options}
                  selected={filter.selected}
                  onChange={filter.onChange}
                />
              ) : filter.type === 'country' ? (
                <CountryFilter
                  key={filter.key}
                  label={filter.label}
                  countries={filter.countries}
                  selected={filter.selected}
                  onChange={filter.onChange}
                />
              ) : (
                <DateRangeSelect
                  key={filter.key}
                  value={filter.value}
                  onChange={filter.onChange}
                  label={filter.label}
                  placeholder={filter.label}
                  calendarOnly
                />
              )
            )}
          </FlexRow>
        )}
      </div>

      {/* Scrollable card list — bottom padding matches sticky footer height */}
      <div
        ref={feedScrollRef}
        className="relative min-h-0 flex-1 overflow-y-auto"
        style={{ paddingBottom: REVIEW_FEED_FOOTER_HEIGHT_PX }}
      >
        <FeedMissingSubscriptionHintOverlay />
        {isBatchAssessing && (
          <div className="sticky top-0 z-10">
            <Progress
              value={assessmentProgress}
              className="h-1 rounded-none bg-tan-400"
              color="yellow"
            />
          </div>
        )}
        {isBatchAssessing && assessmentProgress < 20 ? (
          <BatchAssessingPlaceholder />
        ) : isPending ? (
          <FlexCol gap="4" alignItems="center" className="py-4">
            {[1, 2, 3].map(i => (
              <UpdateCardSkeleton key={i} />
            ))}
          </FlexCol>
        ) : filteredUpdates.length === 0 ? (
          hasActiveFilters ? (
            <FlexCol
              gap="3"
              alignItems="center"
              justifyContent="center"
              className="h-full"
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-neutral-100">
                <Filter size={36} className="text-neutral-400" />
              </div>
              <FlexCol gap="1" alignItems="center">
                <Text
                  variant="body-sm"
                  className="font-medium text-neutral-900"
                >
                  No updates match these filters
                </Text>
                <Text
                  variant="body-sm"
                  color="secondary"
                  className="max-w-[400px] text-center"
                >
                  We couldn't find any regulatory updates that match your
                  current criteria.
                </Text>
              </FlexCol>
              <Button variant="outline" size="sm" onClick={clearAllFilters}>
                Clear all filters
              </Button>
            </FlexCol>
          ) : activeTab === 'to_review' ? (
            <FlexCol
              gap="3"
              alignItems="center"
              justifyContent="center"
              className="h-full"
            >
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-neutral-100">
                <PartyPopper size={36} className="text-neutral-400" />
              </div>
              <FlexCol gap="1" alignItems="center">
                <Text
                  variant="body-sm"
                  className="font-medium text-neutral-900"
                >
                  All caught up!
                </Text>
                <FlexCol gap="1" alignItems="center">
                  <Text
                    variant="body-sm"
                    color="secondary"
                    className="max-w-[400px] text-center"
                  >
                    You've assessed all of your regulatory updates.
                  </Text>
                  <Text
                    variant="body-sm"
                    color="secondary"
                    className="max-w-[400px] text-center"
                  >
                    Check back later for more.
                  </Text>
                </FlexCol>
              </FlexCol>
            </FlexCol>
          ) : (
            <FlexCol gap="1" alignItems="center" className="py-12">
              <Text variant="body-sm" color="secondary">
                No updates in this category
              </Text>
            </FlexCol>
          )
        ) : (
          <FlexCol gap="4" alignItems="center" className="py-4">
            <TabDescription activeTab={activeTab} />
            {newsfeedDisplayItems.map((item, index) => {
              const update = item.item;
              const isGhost = item.kind === 'ghost';
              const exitPhase = item.exit?.phase ?? null;
              const rowKey = item.exit
                ? `exit-${item.id}-${item.exit.seq}`
                : item.id;
              const shouldAnimate =
                !isGhost &&
                !item.exit &&
                (animateCards || newBatchIds.has(update.id));
              const liveIndex = item.kind === 'live' ? item.index : 0;

              let exitClass = '';
              if (exitPhase === 'animating') {
                exitClass = 'animate-newsfeed-card-exit pointer-events-none';
              } else if (exitPhase === 'hold' || exitPhase === 'collapse') {
                exitClass = 'invisible pointer-events-none';
              }

              return (
                <ExitCollapseRow
                  key={rowKey}
                  phase={exitPhase}
                  collapseDurationMs={NEWSFEED_EXIT_COLLAPSE_MS}
                  className="w-full max-w-[800px]"
                  ariaHidden={isGhost}
                >
                  <div
                    className={cn(
                      shouldAnimate && 'animate-slide-in-right opacity-0',
                      exitClass
                    )}
                    style={
                      shouldAnimate
                        ? {
                            animationDelay: animateCards
                              ? `${index * 80}ms`
                              : '0ms',
                          }
                        : undefined
                    }
                  >
                    <UpdateCard
                      update={update}
                      isNew={!hasSeen(update.id)}
                      onClick={
                        isGhost
                          ? () => {}
                          : () => handleCardClick(update, liveIndex)
                      }
                      onStatusChange={
                        !isGhost && update.context
                          ? status => handleStatusChangeFromFeed(update, status)
                          : undefined
                      }
                    />
                  </div>
                </ExitCollapseRow>
              );
            })}
            {activeTab === 'to_review' && (
              <p className={FEED_DESCRIPTOR_CLASS}>{TO_REVIEW_FEED_END_COPY}</p>
            )}
          </FlexCol>
        )}
      </div>

      <ReviewFeedFooter
        activeTab={activeTab}
        tabCardCount={tabCardCount}
        filteredUpdates={filteredUpdates}
        hasSeen={hasSeen}
        bulkApplyStatuses={bulkApplyStatuses}
        isBulkApplying={isBulkApplying}
      />
    </div>
  );
};

const ReviewFeedFooter: FC<{
  activeTab: StatusTab;
  tabCardCount: number;
  filteredUpdates: ListUpdatesResponse[];
  hasSeen: (id: string) => boolean;
  bulkApplyStatuses: ReturnType<
    typeof useBulkApplyContextStatusesService
  >['bulkApplyStatuses'];
  isBulkApplying: boolean;
}> = ({
  activeTab,
  tabCardCount,
  filteredUpdates,
  hasSeen,
  bulkApplyStatuses,
  isBulkApplying,
}) => {
  const metrics = useMemo(() => {
    if (activeTab !== 'to_review') return null;
    let newCount = 0;
    let recApplicable = 0;
    let recNotApplicable = 0;
    const applyTargets: ListUpdatesResponse[] = [];
    for (const u of filteredUpdates) {
      if (!hasSeen(u.id)) newCount++;
      const rec = u.context?.recommendedApplicability;
      const generating = u.context?.aiImpactStatus === 'generating';
      if (!generating && rec === 'applicable') recApplicable++;
      if (!generating && rec === 'not_applicable') recNotApplicable++;
      if (
        u.context &&
        !generating &&
        (rec === 'applicable' || rec === 'not_applicable')
      ) {
        applyTargets.push(u);
      }
    }
    return {
      newCount,
      recApplicable,
      recNotApplicable,
      applyTargets,
      applyCount: applyTargets.length,
    };
  }, [activeTab, filteredUpdates, hasSeen]);

  const handleApplyRecommendations = useCallback(() => {
    if (!metrics || metrics.applyCount === 0) return;
    bulkApplyStatuses(
      metrics.applyTargets.map(u => {
        const status = u.context!.recommendedApplicability;
        return {
          id: u.context!.id,
          status: status as 'applicable' | 'not_applicable',
        };
      })
    );
  }, [metrics, bulkApplyStatuses]);

  return (
    <footer
      className="flex shrink-0 w-full items-center border-t border-neutral-200 bg-white px-6"
      style={{
        height: REVIEW_FEED_FOOTER_HEIGHT_PX,
      }}
    >
      <div className="flex min-h-0 w-full items-center justify-between gap-x-4">
        <div className="flex shrink-0 items-center gap-4">
          <span className="shrink-0 text-sm font-normal text-neutral-500 whitespace-nowrap">
            {STATUS_TAB_LABELS[activeTab]}:
          </span>
          <span className="text-sm font-normal text-neutral-900 whitespace-nowrap">
            {tabCardCount} {tabCardCount === 1 ? 'update' : 'updates'}
          </span>
          {activeTab === 'to_review' && metrics && metrics.newCount > 0 && (
            <span className="text-sm font-medium text-neutral-900 whitespace-nowrap">
              ({metrics.newCount} new)
            </span>
          )}
        </div>
        {activeTab === 'to_review' && metrics && (
          <div className="flex min-h-0 min-w-0 flex-1 items-center justify-end gap-4 overflow-hidden">
            {(metrics.recApplicable > 0 || metrics.recNotApplicable > 0) && (
              <p className="min-w-0 truncate text-right text-sm font-normal text-neutral-900">
                Our analysis found {metrics.recApplicable}{' '}
                {metrics.recApplicable === 1 ? 'update' : 'updates'} applicable,{' '}
                {metrics.recNotApplicable} not applicable
              </p>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 w-auto shrink-0 gap-1.5 px-[12px] py-[5.5px] font-normal"
              disabled={metrics.applyCount === 0 || isBulkApplying}
              onClick={handleApplyRecommendations}
            >
              {isBulkApplying ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <Check className="h-4 w-4 shrink-0" />
              )}
              <span>
                Apply {metrics.applyCount}{' '}
                {metrics.applyCount === 1
                  ? 'recommendation'
                  : 'recommendations'}
              </span>
            </Button>
          </div>
        )}
      </div>
    </footer>
  );
};

const AssessmentCompleteBanner: FC<{
  totalUpdates: number;
  relevantCount: number;
  onDismiss: () => void;
}> = ({ totalUpdates, relevantCount, onDismiss }) => (
  <div className="flex items-center gap-2.5 rounded-lg border border-blue-500 bg-blue-50 p-2.5 text-sm text-neutral-900 text-center">
    <p className="flex-1">
      <span className="font-medium">Your custom feed is ready! </span>
      We filtered {totalUpdates} regulatory updates down to {relevantCount} that
      matter for your device, with impact assessments for each.
    </p>
    <button
      onClick={onDismiss}
      className="shrink-0 p-0.5 rounded hover:bg-blue-100 text-neutral-500 hover:text-neutral-700 transition-colors"
    >
      <X className="h-5 w-5" />
    </button>
  </div>
);

const CYCLING_MESSAGES = [
  'Fetching the latest updates from 4,200+ global sources\u2026',
  'Filtering for what\u2019s relevant to you\u2026',
  'Generating custom impact assessments and priorities\u2026',
];

const CYCLE_INTERVAL_MS = 3000;

const BatchAssessingPlaceholder: FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % CYCLING_MESSAGES.length);
    }, CYCLE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <FlexCol
      gap="4"
      alignItems="center"
      justifyContent="center"
      className="pt-40"
    >
      <FlexCol gap="2" alignItems="center">
        <Text variant="body" className="font-medium text-neutral-900">
          Generating your personalized feed
        </Text>
      </FlexCol>
      <div className="flex items-center gap-2">
        <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
        <Text
          variant="body-sm"
          color="secondary"
          className="transition-opacity duration-300"
        >
          {CYCLING_MESSAGES[activeIndex]}
        </Text>
      </div>
    </FlexCol>
  );
};

const TabDescription: FC<{ activeTab: StatusTab }> = ({ activeTab }) => {
  const descriptions: Record<StatusTab, string> = {
    to_review:
      'Regulatory updates that were flagged as potentially relevant to your devices. Assessments pending validation.',
    applicable:
      'Regulatory updates that were assessed and determined to impact your devices. Actions may be required to maintain compliance.',
    not_applicable:
      'Regulatory updates that were assessed and determined not to impact your devices.',
    out_of_scope:
      'Regulatory updates that fall outside the scope relevant to your devices, such as different jurisdictions or device categories.',
  };

  return (
    <p className="w-max min-w-0 max-w-full truncate text-center text-xs italic tracking-[0.18px] text-neutral-500">
      {descriptions[activeTab]}
    </p>
  );
};

const OptionFilter: FC<{
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const isActive = selected.length > 0;
  const activeLabel =
    selected.length === 1
      ? options.find(o => o.value === selected[0])?.label
      : `${selected.length} selected`;

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={isActive ? activeLabel : undefined}
      onClear={isActive ? () => onChange([]) : undefined}
    >
      <FilterOptionList
        options={options}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};

const SortModeFilter: FC<{
  value: ReviewSortMode;
  onChange: (value: ReviewSortMode) => void;
}> = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="flex items-center gap-2 px-2 py-1 text-sm font-semibold tracking-[-0.01em] text-neutral-900 hover:text-neutral-700 transition-colors whitespace-nowrap">
          Sorted by {REVIEW_SORT_MODE_LABELS[value].toLowerCase()}
          <ArrowUpDown className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto min-w-[200px] p-0" align="end">
        <div className="max-h-[280px] overflow-y-auto">
          <div className="py-1">
            {REVIEW_SORT_OPTIONS.map(option => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.value}
                  onClick={() => {
                    onChange(option.value as ReviewSortMode);
                    setOpen(false);
                  }}
                  className="flex w-full items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-accent focus-visible:outline-none focus-visible:bg-accent"
                >
                  <span className="truncate">{option.label}</span>
                  {isSelected ? (
                    <Check className="h-4 w-4 shrink-0 text-primary" />
                  ) : (
                    <span className="h-4 w-4 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
